import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import quotationService, { Quotation, QuotationRequest } from '@/services/quotation.service';
import { OPPORTUNITY_KEYS } from './useOpportunities';

// Query keys
export const QUOTATION_KEYS = {
  all: ['quotations'] as const,
  lists: () => [...QUOTATION_KEYS.all, 'list'] as const,
  list: (filters: any) => [...QUOTATION_KEYS.lists(), filters] as const,
  details: () => [...QUOTATION_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...QUOTATION_KEYS.details(), id] as const,
};

/**
 * Fetch all quotations with optional filters
 */
export function useQuotations(
  filters?: {
    status?: string;
    opportunity_id?: string;
    customer_id?: string;
    boq_id?: string;
    trading_rental_estimation_id?: string;
    is_latest_revision?: boolean;
    sales_team_id?: string;
    search?: string;
    commercial_stage?: string;
  },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: QUOTATION_KEYS.list(filters),
    queryFn: () => quotationService.getAll(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Fetch quotation by ID
 */
export function useQuotation(id: string) {
  return useQuery({
    queryKey: QUOTATION_KEYS.detail(id),
    queryFn: () => quotationService.getById(id),
    enabled: !!id,
  });
}

/**
 * Create new quotation
 */
export function useCreateQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QuotationRequest) => quotationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create quotation');
    },
  });
}

/**
 * Update quotation
 */
export function useUpdateQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuotationRequest> }) =>
      quotationService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update quotation');
    },
  });
}

/**
 * Delete quotation
 */
export function useDeleteQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete quotation');
    },
  });
}

/**
 * Approve quotation
 */
export function useApproveQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotationService.approve(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation approved successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve quotation');
    },
  });
}

/**
 * Send quotation to customer
 */
export function useSendQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => quotationService.send(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation sent successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to send quotation');
    },
  });
}

/**
 * Accept quotation
 */
export function useAcceptQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, customerSignatureUrl }: { id: string; customerSignatureUrl?: string }) =>
      quotationService.accept(id, customerSignatureUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation accepted');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to accept quotation');
    },
  });
}

/**
 * Reject quotation
 */
export function useRejectQuotation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason: string }) =>
      quotationService.reject(id, rejectionReason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      message.success('Quotation rejected');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject quotation');
    },
  });
}

/**
 * Create quotation revision
 */
export function useCreateQuotationRevision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      revisionReason,
      revisedData,
    }: {
      id: string;
      revisionReason: string;
      revisedData?: Partial<QuotationRequest> & {
        revision_type?: string | null;
        revision_notes?: string | null;
        estimate_source?: 'keep' | 'latest_approved' | 'selected';
        selected_estimation_id?: string | null;
      };
    }) => quotationService.createRevision(id, revisionReason, revisedData),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      const newId =
        (result as { data?: { id?: string }; id?: string })?.data?.id ||
        (result as { id?: string })?.id;
      if (newId) {
        queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(String(newId)) });
      }
      message.success('Quotation revision created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create quotation revision');
    },
  });
}

/**
 * Convert quotation to sales order
 */
export function useConvertQuotationToSalesOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: string | { quotationId: string; departmentId?: string }) => {
      if (typeof vars === 'string') {
        return quotationService.convertToSalesOrder(vars);
      }
      return quotationService.convertToSalesOrder(vars.quotationId, vars.departmentId);
    },
    onSuccess: (data, vars) => {
      const quotationId = typeof vars === 'string' ? vars : vars.quotationId;
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(quotationId) });
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      // Invalidate and refetch all sales orders queries (with any filters) to ensure the new order appears
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      // Refetch all sales orders queries regardless of filters
      queryClient.refetchQueries({ 
        queryKey: ['sales-orders'],
        type: 'active' // Refetch all active queries
      });
      console.log('✅ Quotation converted, sales orders cache invalidated and refetched');
      message.success('Quotation converted to Customer Order successfully');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to convert quotation to customer order';
      console.error('Error converting quotation to sales order:', {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
        fullError: error
      });
      message.error(errorMessage);
    },
    retry: false, // Prevent automatic retries that could cause loops
  });
}

/**
 * Create quotation from BOQ
 */
export function useCreateQuotationFromBOQ() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: {
      boqId: string;
      opportunityId?: string;
      selectedLineIds?: string[];
      revenueServiceTypeId?: string;
      departmentId?: string;
    }) =>
      quotationService.createFromBOQ(vars.boqId, {
        opportunityId: vars.opportunityId,
        selectedLineIds: vars.selectedLineIds,
        revenueServiceTypeId: vars.revenueServiceTypeId,
        departmentId: vars.departmentId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: OPPORTUNITY_KEYS.all });
      // Don't show message here - let the caller handle it
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create quotation from BOQ');
    },
  });
}
